import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import {
  formatDate,
  formatDateBerlinWallClock,
  formatTime,
  formatTimeBerlinWallClock,
} from '~/utils/dateHelpers'
import type { EventDate, EventDateChildren } from '~/composables/types/event'
import type { CartItem } from '~/composables/types/cart'

dayjs.extend(timezone)

export const useEventDateHelpers = () => {
  const { locale } = useI18n()

  type GeneralDate = EventDate | CartItem | EventDateChildren

  const fnGetDateLabelHtml = (
    date: GeneralDate,
    options?: {
      currentDate?: Date
      /** Wall-clock Europe/Berlin; omit UTC→Berlin shift. Defaults true for cart instant-booking lines. */
      dateTimesInBerlinWallClock?: boolean
    }
  ): string => {
    let currentDate = options?.currentDate ?? new Date()
    if (typeof currentDate === 'string') {
      currentDate = new Date(currentDate)
    }

    const start = date?.start || date?.startTime
    const end = date?.end || date?.endTime

    if (!start || !end) {
      return ''
    }

    const wallClock =
      options?.dateTimesInBerlinWallClock ??
      ('instantBooking' in date && date.instantBooking === true)

    const eventDate = wallClock
      ? dayjs.tz(start, 'Europe/Berlin').toDate()
      : new Date(start)
    const dateStr = wallClock
      ? formatDateBerlinWallClock(start, locale.value)
      : formatDate(start, locale.value)

    // Check if all_day exists and is true (only EventDate has this property)
    const all_day = 'all_day' in date && date.all_day

    // Check if the event is more than 12 months after the current date
    const twelveMonthsLater = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 12,
      1
    )

    const shouldIncludeYear = eventDate > twelveMonthsLater
    const yearPart = shouldIncludeYear ? ` ${eventDate.getFullYear()}` : ''

    // For all-day events, only show date (no time)
    if (all_day) {
      return `${dateStr}${yearPart}`
    }

    // For regular events, show date and time
    const startTimeStr = wallClock
      ? formatTimeBerlinWallClock(start)
      : formatTime(start)
    const endTimeStr = wallClock
      ? formatTimeBerlinWallClock(end)
      : formatTime(end)
    return `${dateStr}${yearPart} ${startTimeStr} - ${endTimeStr}`
  }

  const fnGetChildrenHtmlArr = (item: GeneralDate) => {
    return (
      item.children?.data?.map((x: EventDateChildren) =>
        fnGetDateLabelHtml(x)
      ) || []
    )
  }

  return {
    fnGetDateLabelHtml,
    fnGetChildrenHtmlArr,
  }
}

export const getDateTime = (date: EventDate): string => {
  const { t } = useI18n()

  // Check all_day first, before checking for missing values
  const all_day = date?.all_day
  if (all_day) {
    return t('product.show.checkout.allDay')
  }

  const start = date?.start
  const end = date?.end

  if (!start || !end) {
    return ''
  }

  const startTimeStr = formatTime(start)
  const endTimeStr = formatTime(end)
  return `${startTimeStr} - ${endTimeStr}`
}
